/**
 * Widget Area Context
 * Provides global state for widget area visibility
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react'

interface WidgetAreaContextType {
  isOpen: boolean
  toggle: () => void
  open: () => void
  close: () => void
  isEditMode: boolean
  setEditMode: (editMode: boolean) => void
  toggleEditMode: () => void
  isPromptsPanelOpen: boolean
  promptsPanelSelection?: string
  openPromptsPanel: (promptId?: string) => void
  closePromptsPanel: () => void
}

const WidgetAreaContext = createContext<WidgetAreaContextType | null>(null)

export function WidgetAreaProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [promptsPanel, setPromptsPanel] = useState<{ selection?: string } | null>(null)

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev)
    setIsEditMode(false)
    setPromptsPanel(null)
  }, [])

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    // Exit edit mode when closing widget area
    setIsEditMode(false)
    setPromptsPanel(null)
  }, [])

  const openPromptsPanel = useCallback((promptId?: string) => {
    setIsOpen(true)
    setIsEditMode(false)
    setPromptsPanel({ selection: promptId })
  }, [])

  const closePromptsPanel = useCallback(() => {
    setPromptsPanel(null)
    // Focus goes back to where the panel came from, when the widget is in the bar.
    requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-prompts-expand]')?.focus())
  }, [])

  const toggleEditMode = useCallback(() => {
    setIsEditMode(prev => !prev)
  }, [])

  return (
    <WidgetAreaContext.Provider value={{ isOpen, toggle, open, close, isEditMode, setEditMode: setIsEditMode, toggleEditMode, isPromptsPanelOpen: Boolean(promptsPanel), promptsPanelSelection: promptsPanel?.selection, openPromptsPanel, closePromptsPanel }}>
      {children}
    </WidgetAreaContext.Provider>
  )
}

export function useWidgetArea() {
  const context = useContext(WidgetAreaContext)
  if (!context) {
    throw new Error('useWidgetArea must be used within WidgetAreaProvider')
  }
  return context
}
